import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, concat, finalize, map } from 'rxjs';
import { AvatarComponent } from '../avatar/avatar.component';
import { ImageLightboxComponent } from '../image-lightbox/image-lightbox.component';
import { LightboxController } from '../image-lightbox/lightbox-controller';
import { ResPillComponent, ResPillStatus } from '../res-pill/res-pill.component';
import { SectionTitleComponent } from '../section-title/section-title.component';
import { TestRunComment, TestRunCommentAttachment } from '../../models/test-run-comment.model';
import { TestRunCommentService } from '../../services/test-run-comment.service';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { IMAGE_UPLOAD_ACCEPT, ImageUploadRejection, rejectImageUpload } from '../../shared/image-upload';

/**
 * Wohin ein Kommentar zeigt — schon aufgeloest.
 *
 * <p>Fertig aufgeloest statt aus dem Runner-Zustand gelesen: dieselbe Historie
 * haengt laut Story spaeter auch am Report und an der Durchlauf-Liste, wo es
 * keinen Runner gibt. Wer die Aufloesung hat, reicht sie herein; wer nicht, laesst
 * sie weg.
 */
export interface CommentStepReference {
  /** Kennung des Reihenfolge-Elements; {@code null}, wenn es keine traegt. */
  elementKey: string | null;
  elementTitle: string | null;
  /** Anzeige-Nummer des Schritts wie im Runner: „1", „2.1". */
  stepNumber: string;
  status: ResPillStatus;
  /** i18n-Schluessel der Ergebnis-Beschriftung; uebersetzt wird im Template. */
  statusLabelKey: string;
}

/**
 * Ein Eintrag der Historie samt seinem aufgeloesten Bezug. Gepaart statt im
 * Template nachgeschlagen: eine Methode im Template liefe bei jedem
 * Change-Detection-Zyklus erneut ueber alle Eintraege.
 */
interface CommentRow {
  comment: TestRunComment;
  /** {@code null}, wenn der Kommentar keinen Bezug hat oder er nicht auflösbar ist. */
  reference: CommentStepReference | null;
  /** Ob der Eintrag aus einem frueheren Durchlauf stammt als dem gezeigten. */
  previousRound: boolean;
  /**
   * Nummer dieses frueheren Durchlaufs, {@code null}, wenn sie nicht feststeht. Hier
   * aufgeloest statt im Template gegen {@code null} geprueft: ein Backend ohne die
   * Spalte laesst die Felder ganz weg, und {@code undefined} haette die Beschriftung
   * mit einem unersetzten Platzhalter gefuellt.
   */
  roundNr: number | null;
}

/** Welcher Satz zu welcher abgelehnten Datei gehoert. */
const REJECTION_KEYS: Record<ImageUploadRejection, string> = {
  type: 'testRun.comments.fileTypeError',
  size: 'testRun.comments.fileSizeError',
};

/**
 * Die Kommentar-Historie eines Testlaufs (NEX-1115).
 *
 * <p>Eigene Komponente unter {@code src/app/components/} statt eines Blocks im
 * Runner: dieselbe Historie steht laut Story spaeter auch im Report und an der
 * Durchlauf-Liste, und die Regel „aendern und loeschen darf nur der Autor" gehoert
 * dorthin, wo sie gilt.
 *
 * <p>Ohne {@code lib-*}-Grundlage gebaut: die veroeffentlichte
 * {@code @spirit-testing/component-library@1.24.0} bringt weder Button noch
 * Textarea oder Chip mit — nachpruefbar an ihren Selektoren
 * ({@code lib-badge|breadcrumb|header|headline|menu|menu-item|modal|score-card|subheadline|tile}).
 * Dieselbe Lage wie bei {@code test-run-handover-modal}; die Library waechst laut
 * {@code CLAUDE-FE.md} nicht aus dem Frontend heraus, ein LIB-Ticket entfaellt darum.
 *
 * <p>Die Bilder werden erst beim Oeffnen geladen, nicht beim Anzeigen der Liste: Ein
 * Lauf mit vielen Anhaengen zoege sonst genauso viele Downloads nach sich, von denen
 * die meisten niemand ansieht. Einmal geholt, bleibt die Blob-URL bis zum Verlassen
 * der Seite stehen.
 */
@Component({
  selector: 'app-test-run-comments',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    FormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    AvatarComponent,
    ImageLightboxComponent,
    ResPillComponent,
    SectionTitleComponent,
  ],
  templateUrl: './test-run-comments.component.html',
  styleUrl: './test-run-comments.component.scss',
})
export class TestRunCommentsComponent implements OnInit {
  private readonly commentService = inject(TestRunCommentService);
  private readonly destroyRef = inject(DestroyRef);

  readonly runId = input.required<string>();

  /**
   * Wer gerade angemeldet ist. {@code null}, solange das Profil nicht feststeht —
   * dann zeigt die Historie keine Autor-Aktionen an, statt sie zu raten.
   */
  readonly currentUserId = input<string | null>(null);

  /**
   * Ob das Formular angeboten wird. Der Runner entscheidet das: ein automatisierter
   * Lauf wird nicht kommentiert, ein abgeschlossener nimmt nichts mehr an, und eine
   * nur lesende Rolle bekaeme vom Backend ein 403.
   */
  readonly canComment = input(false);

  /**
   * Die Aufloesung {@code stepResultId} → Bezug. Kommentare aus frueheren Runden
   * zeigen auf Schrittergebnisse, die der aktuelle Durchlauf nicht kennt; sie
   * fehlen hier und fallen damit auf den allgemeinen Hinweis zurueck — genau wie
   * jeder Kommentar, wenn die Eingabe ganz ausbleibt.
   */
  readonly stepReferences = input<Record<string, CommentStepReference>>({});

  /**
   * Der Durchlauf, den die Seite gerade zeigt. Die Historie gehoert dem Lauf und
   * traegt deshalb auch die Notizen aller frueheren Runden; ohne diesen Bezug stuenden
   * sie ununterscheidbar neben den frischen. {@code null} heisst „kein Durchlauf im
   * Blick" — dann bleibt alles unbeschriftet, statt etwas zu behaupten.
   */
  readonly executionId = input<string | null>(null);

  readonly textMaxLength = FIELD_LENGTHS.FREETEXT_MAX;
  readonly imageAccept = IMAGE_UPLOAD_ACCEPT;

  readonly comments = signal<TestRunComment[]>([]);
  readonly loading = signal(true);
  /** i18n-Schluessel der zuletzt gescheiterten Aktion; {@code null} = alles gut. */
  readonly errorKey = signal<string | null>(null);
  readonly submitting = signal(false);

  readonly draft = signal('');
  readonly pendingFiles = signal<File[]>([]);

  readonly editingId = signal<string | null>(null);
  readonly editDraft = signal('');

  private readonly objectUrls = new Map<string, string>();

  private readonly heading = viewChild('heading', { read: ElementRef<HTMLElement> });

  /**
   * Der Anhang formatfuellend. Faellt der Auslöser weg — ein geloeschtes Bild nimmt
   * seinen Knopf mit —, uebernimmt die Ueberschrift den Fokus.
   */
  readonly lightbox = new LightboxController(() => this.heading()?.nativeElement ?? null);

  readonly rows = computed<CommentRow[]>(() => {
    const references = this.stepReferences();
    const currentRound = this.executionId() ?? null;
    return this.comments().map((comment) => {
      const round = comment.executionId ?? null;
      return {
        comment,
        reference: (comment.stepResultId === null ? undefined : references[comment.stepResultId]) ?? null,
        previousRound: currentRound !== null && round !== null && round !== currentRound,
        roundNr: comment.executionNr ?? null,
      };
    });
  });

  readonly empty = computed(() => !this.loading() && this.comments().length === 0);
  readonly canSubmit = computed(() => this.draft().trim().length > 0 && !this.submitting());

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      for (const url of this.objectUrls.values()) URL.revokeObjectURL(url);
    });
    this.load();
  }

  /**
   * Holt die Historie neu. Der Entwurf im Formular bleibt dabei stehen: Nachladen ist
   * eine Anzeigesache und darf nichts wegwischen, was noch nicht abgeschickt ist.
   */
  load(): void {
    this.loading.set(true);
    this.call(
      this.commentService.list(this.runId()).pipe(finalize(() => this.loading.set(false))),
      'testRun.comments.loadError',
      (comments) => this.comments.set(comments),
    );
  }

  isMine(comment: TestRunComment): boolean {
    const me = this.currentUserId();
    return me !== null && comment.authorUserId === me;
  }

  isEdited(comment: TestRunComment): boolean {
    return comment.editedAt !== null;
  }

  onFilesPicked(input: HTMLInputElement): void {
    this.addFiles(Array.from(input.files ?? []));
    // Zuruecksetzen, damit dieselbe Datei direkt danach erneut gewaehlt werden kann.
    input.value = '';
  }

  removePendingFile(index: number): void {
    this.pendingFiles.update((pending) => pending.filter((_, i) => i !== index));
  }

  submit(): void {
    const text = this.draft().trim();
    if (text.length === 0 || this.submitting()) return;

    const files = this.pendingFiles();
    this.submitting.set(true);
    const round = this.executionId();
    this.call(
      this.commentService.create(this.runId(), round === null ? { text } : { text, executionId: round }),
      'testRun.comments.createError',
      (created) => {
        // Angelegt ist angelegt: Der Eintrag gehoert in die Liste, bevor die Bilder
        // daran haengen. Beides in einer Kette liesse ein gescheitertes Bild den
        // Kommentar unsichtbar im Backend zurueck — und der naechste Klick auf
        // „Kommentieren" legte ihn ein zweites Mal an.
        this.comments.update((comments) => [...comments, created]);
        this.draft.set('');
        this.uploadPending(created.id, files);
      },
      () => this.submitting.set(false),
    );
  }

  startEdit(comment: TestRunComment): void {
    this.editingId.set(comment.id);
    this.editDraft.set(comment.text);
  }

  cancelEdit(): void {
    this.editingId.set(null);
    this.editDraft.set('');
  }

  saveEdit(comment: TestRunComment): void {
    const text = this.editDraft().trim();
    if (text.length === 0) return;
    this.call(
      this.commentService.update(this.runId(), comment.id, { text }),
      'testRun.comments.updateError',
      (updated) => {
        this.replace(updated);
        this.cancelEdit();
      },
    );
  }

  remove(comment: TestRunComment): void {
    this.call(this.commentService.delete(this.runId(), comment.id), 'testRun.comments.deleteError', () => {
      this.comments.update((comments) => comments.filter((c) => c.id !== comment.id));
      // Der Fokus sass auf einem Knopf, den es nicht mehr gibt; ohne dieses Ziel
      // faellt er auf <body> und die Tastaturbedienung beginnt von vorn.
      this.heading()?.nativeElement.focus();
    });
  }

  removeAttachment(attachment: TestRunCommentAttachment): void {
    this.call(
      this.commentService.deleteAttachment(this.runId(), attachment.commentId, attachment.id),
      'testRun.comments.attachmentError',
      () => this.dropAttachment(attachment),
    );
  }

  openAttachment(attachment: TestRunCommentAttachment, opener: HTMLElement): void {
    const known = this.objectUrls.get(attachment.id);
    if (known) {
      this.lightbox.open(known, opener);
      return;
    }
    this.call(
      this.commentService.downloadAttachment(this.runId(), attachment.commentId, attachment.id),
      'testRun.comments.attachmentError',
      (blob) => {
        const url = URL.createObjectURL(blob);
        this.objectUrls.set(attachment.id, url);
        this.lightbox.open(url, opener);
      },
    );
  }

  /**
   * Nimmt nur an, was auch das Backend annimmt. Ohne diese Pruefung antwortet der
   * Server mit 413 oder 415, und im Formular steht ein Satz ueber den Kommentar statt
   * einer Auskunft ueber die Datei.
   */
  private addFiles(files: readonly File[]): void {
    if (files.length === 0) return;
    const accepted: File[] = [];
    let rejection: ImageUploadRejection | null = null;
    for (const file of files) {
      const reason = rejectImageUpload(file);
      if (reason) rejection ??= reason;
      else accepted.push(file);
    }
    this.errorKey.set(rejection === null ? null : REJECTION_KEYS[rejection]);
    if (accepted.length > 0) this.pendingFiles.update((pending) => [...pending, ...accepted]);
  }

  /**
   * Ein Aufruf ans Backend samt Fehlerbehandlung.
   *
   * <p>Jede Aktion dieser Historie hat denselben Ablauf: alten Fehler abraeumen,
   * aufrufen, bei Erfolg den Zustand nachziehen, sonst einen Satz anzeigen. Einzeln
   * ausgeschrieben stand dieses Geruest fuenfmal da, und ein vergessenes
   * {@code takeUntilDestroyed} faellt in der Wiederholung niemandem auf.
   */
  private call<T>(source: Observable<T>, errorKey: string, onNext: (value: T) => void, onError?: () => void): void {
    this.errorKey.set(null);
    source.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: onNext,
      error: () => {
        this.errorKey.set(errorKey);
        onError?.();
      },
    });
  }

  /**
   * Haengt die gewaehlten Bilder nacheinander an den angelegten Kommentar.
   *
   * <p>Nacheinander und nicht nebenlaeufig: Die Reihenfolge der Anhaenge ist die, in
   * der sie gewaehlt wurden, und das Backend vergibt sie ueber {@code createdAt}.
   *
   * <p>Jedes Bild wird einzeln nachgetragen und aus dem Formular genommen. Scheitert
   * eines, bleiben die uebrigen stehen und koennen erneut abgeschickt werden, ohne dass
   * die bereits hochgeladenen ein zweites Mal hochgehen.
   */
  private uploadPending(commentId: string, files: readonly File[]): void {
    if (files.length === 0) {
      this.submitting.set(false);
      return;
    }
    const uploads = files.map((file) =>
      this.commentService.uploadAttachment(this.runId(), commentId, file).pipe(map((uploaded) => ({ file, uploaded }))),
    );
    this.call(
      concat(...uploads).pipe(finalize(() => this.submitting.set(false))),
      'testRun.comments.attachmentError',
      ({ file, uploaded }) => {
        this.attach(commentId, uploaded);
        this.pendingFiles.update((pending) => pending.filter((p) => p !== file));
      },
    );
  }

  private attach(commentId: string, uploaded: TestRunCommentAttachment): void {
    this.comments.update((comments) =>
      comments.map((comment) =>
        comment.id === commentId ? { ...comment, attachments: [...comment.attachments, uploaded] } : comment,
      ),
    );
  }

  private dropAttachment(attachment: TestRunCommentAttachment): void {
    const url = this.objectUrls.get(attachment.id);
    if (url) {
      // Der Anhang ist fort, das Bild dahinter erreicht niemand mehr — ohne dieses
      // Freigeben laege es bis zum Verlassen der Seite im Speicher.
      URL.revokeObjectURL(url);
      this.objectUrls.delete(attachment.id);
      this.lightbox.closeIfShowing(url);
    }
    this.comments.update((comments) =>
      comments.map((comment) =>
        comment.id === attachment.commentId
          ? { ...comment, attachments: comment.attachments.filter((a) => a.id !== attachment.id) }
          : comment,
      ),
    );
  }

  private replace(updated: TestRunComment): void {
    this.comments.update((comments) => comments.map((c) => (c.id === updated.id ? updated : c)));
  }
}
